import { gql } from 'apollo-boost';
import Carousel from 'core/components/carousel';
import { NextButton, PreviousButton } from 'core/components/carousel/controls';
import { useIdsFromUrl } from 'core/hooks/useIdsFromUrl';
import { title } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { isMobile } from 'core/utils';
import React, { useCallback, useMemo, useState } from 'react';
import { FeedbackToProductOrderEnum, GetFeedbacksToProductQuery, useGetFeedbacksToProductQuery } from 'site/graphql';
import FeedbackDescription from './FeedbackDescription';
import FeedbackDescriptionModal from './FeedbackDescriptionModal';
import FeedbackHeader from './FeedbackHeader';
import { StarColorEnum } from './Star';
import StarRating from './StarRating';

gql`
    query GetFeedbacksToProduct($filter: FeedbackToProductListRequest) {
        feedbackToProducts(filter: $filter) {
            count
            result {
                id
                accountName
                rating
                date
                dateUpdate
                description
            }
        }
    }
`;

type IFeedback = GetFeedbacksToProductQuery['feedbackToProducts']['result'][0];

const Feedbacks: React.FC<WithStyles<typeof styles>> = ({ classes }) => {
    const { productId } = useIdsFromUrl('productId');
    const [isReadMoreModalOpen, setIsReadMoreModalOpen] = useState(false);
    const [feedbackToProduct, setFeedbackToProduct] = useState<IFeedback>(null);

    const { data } = useGetFeedbacksToProductQuery({
        variables: {
            filter: {
                order: FeedbackToProductOrderEnum.Date,
                direction: 'desc',
                productId: Number(productId),
                full: true,
            },
        },
    });
    const feedbacks = React.useMemo(
        () => (data && data.feedbackToProducts && data.feedbackToProducts.result) || [],
        [data],
    );

    const handleCloseModal = useCallback(() => setIsReadMoreModalOpen(false), []);

    const handleReadMoreClick = useCallback((feedbackToProduct: IFeedback) => {
        setIsReadMoreModalOpen(true);
        setFeedbackToProduct(feedbackToProduct);
    }, []);

    const customControlStyles = useMemo<React.CSSProperties>(
        () => ({
            fill: 'black',
            opacity: isMobile() ? 0.2 : undefined,
            cursor: feedbacks.length > 1 ? undefined : 'default',
        }),
        [feedbacks],
    );

    if (feedbacks.length === 0) {
        return (
            <>
                <h2 className={classes.headerTitle}>Отзывы ({feedbacks.length})</h2>
                <div>У курса пока нет отзывов</div>
            </>
        );
    }

    return (
        <>
            <h2 className={classes.headerTitle}>Отзывы ({feedbacks.length})</h2>
            <div className={classes.averageRating}>
                <div className={classes.averageRatin__header}>Средний рейтинг</div>
                <StarRating
                    starsSelected={
                        feedbacks.map(feedback => feedback.rating).reduce((a, b) => a + b, 0) / feedbacks.length
                    }
                    isSelectionDisabled
                    classname={classes.feedback__rating}
                    color={StarColorEnum.black}
                    starsCustomStyles={{
                        height: 23,
                        width: 23,
                        cursor: 'default',
                        marginRight: 6,
                    }}
                />
            </div>

            <Carousel
                swiping
                slidesToShow={1}
                speed={250}
                wrapAround={feedbacks.length > 1}
                dragging={false}
                renderBottomCenterControls={null}
                renderCenterLeftControls={props => <PreviousButton {...props} customStyles={customControlStyles} />}
                renderCenterRightControls={props => <NextButton {...props} customStyles={customControlStyles} />}
                decoratorStyleCenterLeftControls={{ left: -35 }}
                decoratorStyleCenterRightControls={{ right: -35 }}
                className={classes.feedback__block}
            >
                {feedbacks.map(f2p => (
                    <div key={f2p.id} className={classes.feedback}>
                        <FeedbackHeader feedbackToProduct={f2p} />
                        <FeedbackDescription feedbackToProduct={f2p} onReadMore={handleReadMoreClick} />
                    </div>
                ))}
            </Carousel>

            <FeedbackDescriptionModal
                feedbackToProduct={feedbackToProduct}
                onCloseModal={handleCloseModal}
                isOpen={isReadMoreModalOpen}
            />
        </>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        headerTitle: {
            ...title({ size: 'h2' }),
            marginTop: 2 + 'rem',
            marginBottom: 0,
            lineHeight: 2 + 'rem',

            [theme.breakpoints.down('sm')]: {
                textAlign: 'center',
                fontSize: 1.25 + 'rem',
            },
        },
        feedback: {
            height: 240,
            fontSize: 0.9 + 'rem',
        },
        feedback__rating: {
            display: 'inline-block',
            marginLeft: 20,

            [theme.breakpoints.down('sm')]: {
                textAlign: 'center',
            },
        },
        averageRating: {
            fontWeight: 600,
            fontSize: 1.25 + 'rem',
            marginBottom: 1.25 + 'rem',
            marginTop: 1 + 'rem',

            [theme.breakpoints.down('sm')]: {
                textAlign: 'center',
            },
        },
        averageRatin__header: {
            display: 'inline',

            [theme.breakpoints.down('sm')]: {
                display: 'block',
            },
        },
        feedback__block: {
            [theme.breakpoints.down(1260)]: {
                maxWidth: 'calc(100% - 70px)',
                marginLeft: 'auto',
                marginRight: 'auto',
            },
        },
    });

export default injectSheet(styles, 'Feedbacks')(Feedbacks);
